import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown, Modal, Space } from 'antd';
import { LucideKeyRound } from 'lucide-react';
import { TbDotsVertical, TbEdit, TbRefresh, TbTrash } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel, MiddleDot } from '@/shared';
import RenameResourceModal from '@/shared/components/modals/RenameResourceModal';
import { DeletePrimitiveModal } from '@/shared/components/modals/ResourceActionModals';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { APIKey, APIKeyTimeLength } from '@/types';
import { dayjs, formatDate } from '@/utilities';

import { maskCredential } from '../../../components/credentialPreview';
import DeveloperCredentialListItem from '../../../components/DeveloperCredentialListItem';
import DeveloperCredentialPreview from '../../../components/DeveloperCredentialPreview';

export type APIKeyListRecord = APIKey & { keyPreview?: string };

interface APIKeyListItemProps {
    item: APIKeyListRecord;
    onCredentialGenerated?: (credential: string, apiKeyName: string) => void;
    onDeleted?: (apiKeyId: string) => void;
    onUpdated?: (apiKey: APIKeyListRecord) => void;
}

function expirationLabel(item: APIKeyListRecord) {
    if (!item.createdAt || item.timeLength === APIKeyTimeLength.NEVER_EXPIRES) {
        return 'Never expires';
    }

    const createdAt = dayjs(item.createdAt);
    const expiresAt = {
        [APIKeyTimeLength.ONE_WEEK]: createdAt.add(1, 'week'),
        [APIKeyTimeLength.THIRTY_DAYS]: createdAt.add(30, 'days'),
        [APIKeyTimeLength.THREE_MONTHS]: createdAt.add(3, 'months'),
        [APIKeyTimeLength.ONE_YEAR]: createdAt.add(1, 'year'),
    }[item.timeLength as Exclude<APIKeyTimeLength, APIKeyTimeLength.NEVER_EXPIRES>];

    return expiresAt ? `Expires ${expiresAt.format('MMM D, YYYY')}` : 'Expiration unavailable';
}

const APIKeyListItem: FC<APIKeyListItemProps> = ({ item, onCredentialGenerated, onDeleted, onUpdated }) => {
    const { token } = useAntDesignTheme();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const maskedKey = item.keyPreview || item.key;

    const deleteKey = async () => {
        try {
            await authorizedAxiosInstance.delete(`/api/apikeys/${item.id}`);
            onDeleted?.(item.id);
            toast('API Key Deleted', { description: item.name });
        } catch (error) {
            captureException(error);
            toast.error('Unable to delete API key');
            throw error;
        }
    };

    const rotateKey = () => {
        Modal.confirm({
            title: 'Rotate API key?',
            content: 'The current key will stop working immediately.',
            okText: 'Rotate Key',
            onOk: async () => {
                try {
                    const { data } = await authorizedAxiosInstance.post<APIKeyListRecord>(
                        `/api/apikeys/${item.id}/rotate`,
                    );
                    const keyPreview = data.keyPreview || maskCredential(data.key, { prefixLength: 11 });

                    onUpdated?.({ ...data, key: keyPreview, keyPreview });
                    onCredentialGenerated?.(data.key, data.name);
                } catch (error) {
                    captureException(error);
                    toast.error('Unable to rotate API key');
                    throw error;
                }
            },
        });
    };

    return (
        <>
            <DeveloperCredentialListItem
                itemKey={item.id}
                extra={
                    <Space.Compact block>
                        <DeveloperCredentialPreview value={maskedKey} />
                        <Dropdown
                            trigger={['click']}
                            placement='bottomRight'
                            popupRender={() => (
                                <DropdownMenuPanel
                                    {...{
                                        items: [
                                            { key: 'rename', icon: <TbEdit size='1rem' />, label: 'Rename' },
                                            { type: 'divider' },
                                            { key: 'rotate', icon: <TbRefresh size='1rem' />, label: 'Rotate Key' },
                                            {
                                                key: 'delete',
                                                danger: true,
                                                icon: <TbTrash size='1rem' />,
                                                label: 'Delete',
                                            },
                                        ],
                                        onClick: ({ key }) => {
                                            if (key === 'rename') {
                                                setRenameOpen(true);
                                            }
                                            if (key === 'rotate') {
                                                rotateKey();
                                            }
                                            if (key === 'delete') {
                                                setDeleteOpen(true);
                                            }
                                        },
                                    }}
                                />
                            )}
                        >
                            <Button
                                aria-label='API key actions'
                                size='small'
                                icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                            />
                        </Dropdown>
                    </Space.Compact>
                }
                avatarIcon={<LucideKeyRound size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                name={item.name}
                status='Active'
                description={
                    <>
                        {item.createdAt && (
                            <>
                                Created {formatDate(item.createdAt)}
                                <MiddleDot horizontalMargin='0.5rem' />
                            </>
                        )}
                        {expirationLabel(item)}
                    </>
                }
            />
            <RenameResourceModal
                open={renameOpen}
                currentName={item.name}
                resourceLabel='API Key'
                onCancel={() => setRenameOpen(false)}
                onRename={async (name) => {
                    const { data } = await authorizedAxiosInstance.put<APIKeyListRecord>(`/api/apikeys/${item.id}`, {
                        name,
                    });
                    onUpdated?.(data);
                    setRenameOpen(false);
                    toast('API Key Renamed', { description: name });
                }}
            />
            <DeletePrimitiveModal
                open={deleteOpen}
                resourceLabel='API Key'
                resourceName={item.name}
                warning='Deleting this API key immediately revokes every integration that uses it.'
                onCancel={() => setDeleteOpen(false)}
                onDelete={deleteKey}
            />
        </>
    );
};

export default APIKeyListItem;
